import moment from 'moment';

import { SIGNUP_SOURCES } from 'src/app/components/Pages/GetStarted/consts';
import type { CreateAccountWithDoctorTransferAndAllMeds, CreateAccountWithTransfer } from 'src/app/state/actions';
import { CURRENT_CAPSULE_TOS_VERSION, SIGNUP_FORM } from 'src/consts';

const FULL_PROFILE_TRANSFER_IN = 'All Medications';

export async function loadAccountData(client, { accountId }) {
  return await client.account.get(accountId, ['user', 'delivery_address', 'patient']);
}

export async function loadPHKDeliveryAddress(client) {
  return await client.deliveryAddress.list();
}

// New Account Dispatches
export async function fetchAccount(client, { accountId }) {
  return await client.bffAccount(accountId);
}

export async function fetchPatient(client, { accountId }) {
  return await client.bffPatient(accountId);
}

export async function fetchPaymentCards(client, { accountId }) {
  return await client.bffGetPaymentCards(accountId);
}

export async function fetchDeliveryAddress(client, { accountId }) {
  return await client.bffDeliveryAddress(accountId);
}

export async function fetchActivePending(client, { accountId }) {
  return await client.bffActiveAndPendingMedications(accountId);
}

export const fetchActiveHomeItems = async (client, accountId: string) => await client.bffActiveHomeItems(accountId);

export async function fetchArchivedMedications(client, { accountId }) {
  return await client.bffArchivedMedications(accountId);
}

export async function fetchPendingTransferItems(client, { accountId }) {
  return await client.bffPendingTransfers(accountId);
}

export async function accountCreateCard(client, { accountId, cardToken }) {
  return await client.bffCreatePaymentCard(accountId, { cardToken });
}

export async function accountDeleteCard(client, { accountId, cardId }) {
  return await client.bffDeletePaymentCard(accountId, cardId);
}

export async function accountDeleteAddress(client, { addressId, accountId }) {
  return await client.account.deleteAccountAddress(addressId, {
    data: {
      attributes: {
        is_active: false,
      },
      type: 'DeliveryAddress',
      relationships: {
        account: {
          data: {
            id: { accountId },
            type: 'Account',
          },
        },
      },
    },
  });
}

export async function accountOnboardingValidate(client, data) {
  const {
    email,
    password,
    phone,
    signupSource,
    selfAttribution = null,
    agreedToTermsVersion = null,
    referral = null,
  } = data;

  return await client.account.onboardingValidate({
    user: {
      email,
      password,
    },
    account: {
      phone,
      signupSource,
      selfAttribution,
      agreedToTermsVersion,
      referral,
    },
  });
}

export async function accountSetUploadPhotoID(client, { accountId, file }) {
  const response = await client.bffAccountUploadPhotoId(accountId, file);
  return response;
}

const getExternalSource = (promoCode: string, externalId: string) => {
  const EXTERNAL_SOURCE_PROMO_CODE_MAP = {
    chicagotransit: 'cta',
  };

  if (!externalId) return null;
  if (!EXTERNAL_SOURCE_PROMO_CODE_MAP.hasOwnProperty(promoCode)) return null;
  return EXTERNAL_SOURCE_PROMO_CODE_MAP[promoCode];
};

/**
 *
 * @param client
 * @param requestBody CreateAccountWithTransfer
 * @returns API Response
 */
export async function accountCreateWithTransfer(client, data: CreateAccountWithTransfer) {
  const {
    firstName,
    lastName,
    sex,
    birthdate,
    email,
    password,
    phone,
    medications = [],
    pharmacyName,
    pharmacyDetails,
    zipCode,
    signupSource = SIGNUP_FORM.source,
    referral,
    employeeBadge,
  } = data;
  const birthDateFormatted = moment(birthdate, 'MM/DD/YYYY').format('YYYY-MM-DD');
  const patientData = {
    firstName,
    lastName,
    birthdate: birthDateFormatted,
    sex,
    allergies: 'None',
    medicalConditions: 'None',
    otherMedications: 'None',
  };

  const rxTransfers = medications.map(medication => ({
    ...medication,
    pharmacyDetails,
    pharmacyName,
    source: 'pharmacy',
  }));

  return await client.account.onboarding({
    user: {
      email,
      password,
      isLoginSet: true,
    },
    account: {
      phone,
      signupSource,
      selfAttribution: null,
      agreedToTermsVersion: CURRENT_CAPSULE_TOS_VERSION,
      referral,
      zipCode,
      externalId: employeeBadge,
      externalSource: getExternalSource(referral, employeeBadge),
    },
    patient: patientData,
    prescriptionTransfers: rxTransfers,
  });
}

export async function accountSingleStepTransferCreate(
  client: any,
  values: CreateAccountWithDoctorTransferAndAllMeds,
  user_values: {
    email: string;
    password: string;
  },
  useMatchEndpoint: boolean
) {
  const {
    firstName,
    lastName,
    pharmacyName,
    pharmacyDetails,
    pharmacyPhone,
    phone,
    sex,
    birthdate,
    medications,
    allergies,
    zipCode,
    accountId,
    doctorName,
    doctorPhone,
    referral,
    shouldTransferAllMeds,
  } = values;
  const { email, password } = user_values;
  const birthDateFormatted = moment(birthdate, 'MM/DD/YYYY').format('YYYY-MM-DD');

  const rxTransfers = medications.map(medication => {
    if (!doctorName) {
      return {
        medicationName: medication.medicationName,
        hiclSeqno: medication.hiclSeqno,
        pharmacyDetails,
        pharmacyName,
        pharmacyPhone,
        source: 'pharmacy',
      };
    } else {
      return {
        medicationName: medication.medicationName,
        hiclSeqno: medication.hiclSeqno,
        source: 'doctor',
        doctorName: doctorName,
        doctorPhone: doctorPhone,
        npi: medication.doctorNpi,
      };
    }
  });

  if (accountId) {
    const data = {
      user: {
        email: email,
        password: password,
      },
      account: {
        phone: phone,
        signup_source: values.signupSource ? values.signupSource : SIGNUP_SOURCES.WEB_ACCOUNT_CREATION,
        agreed_to_terms_version: CURRENT_CAPSULE_TOS_VERSION,
        zip_code: zipCode,
        referral: referral,
      },
      patient: {
        first_name: firstName,
        last_name: lastName,
        birthdate,
        sex: sex,
        allergies: allergies,
        medical_conditions: null,
        other_medications: null,
      },
      prescription_transfers: shouldTransferAllMeds
        ? [
            {
              medicationName: FULL_PROFILE_TRANSFER_IN,
              hiclSeqno: null,
              pharmacyDetails,
              pharmacyName,
              pharmacyPhone,
              source: 'pharmacy',
            },
          ]
        : rxTransfers,
      type: 'Onboarding',
    };
    return await client.account.onboardingUpdate(accountId, data);
  }

  const endpoint = useMatchEndpoint ? client.account.onboardingWithMatch : client.account.onboarding;

  return await endpoint({
    user: {
      email,
      password,
      isLoginSet: true,
    },
    account: {
      phone,
      signupSource: values.signupSource ? values.signupSource : SIGNUP_SOURCES.WEB_ACCOUNT_CREATION,
      selfAttribution: null,
      agreedToTermsVersion: CURRENT_CAPSULE_TOS_VERSION,
      referral: referral,
      zipCode,
    },
    prescriptionTransfers: shouldTransferAllMeds
      ? [
          {
            medicationName: FULL_PROFILE_TRANSFER_IN,
            hiclSeqno: null,
            pharmacyDetails,
            pharmacyName,
            pharmacyPhone,
            source: 'pharmacy',
          },
        ]
      : rxTransfers,
    patient: {
      firstName,
      lastName,
      birthdate: birthDateFormatted,
      sex,
      allergies,
    },
  });
}

export function accountObtainMagicToken(client) {
  return client.account.obtainMagicToken();
}

export async function notifyTransfer(client, { accountId }) {
  return await client.notifyTransfer(accountId);
}

export async function acceptTos(client, { accountId, userId }) {
  return await client.account.patch(accountId, {
    data: {
      id: accountId,
      type: 'Account',
      attributes: {
        agreed_to_terms_version: CURRENT_CAPSULE_TOS_VERSION,
      },
      relationships: {
        user: {
          data: {
            id: userId,
            type: 'User',
          },
        },
      },
    },
  });
}

export async function updateAccountLogin(client, { userId, email, password }) {
  return await client.user.patch(userId, {
    data: {
      id: userId,
      type: 'User',
      attributes: {
        email,
        password,
        is_login_set: true,
      },
    },
  });
}
